import React, { useEffect, useState } from "react";
import api from "../services/api";
import { 
  TerminalSquare, Plus, Trash2, X, Code, Save, 
  AlertCircle, Tags, BrainCircuit, Users, CheckCircle, XCircle
} from "lucide-react";
import toast from "react-hot-toast";
import Sidebar from "../components/Sidebar";


export default function TeacherCodingArena() {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [viewingSubmissions, setViewingSubmissions] = useState(false);
  const [activeProblemTitle, setActiveProblemTitle] = useState("");
  const [subsLoading, setSubsLoading] = useState(false);
  const [subSearch, setSubSearch] = useState("");

  // Form State
  const [formData, setFormData] = useState({
    title: "",
    difficulty: "Easy",
    prompt: "",
    tags: "",
    pythonStarter: "def solution():\n    # Read inputs via input() or define logic\n    pass",
    javaStarter: "import java.util.*;\npublic class Solution {\n    public static void main(String[] args) {\n        // logic here\n    }\n}",
    cases: [{ input: "", expected: "" }]
  });

  const load = () => {
    setLoading(true);
    api.get("/coding/problems")
      .then(res => setProblems(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCaseChange = (idx, field, val) => {
    const newCases = [...formData.cases];
    newCases[idx][field] = val;
    setFormData({ ...formData, cases: newCases });
  };

  const addCase = () => setFormData({ ...formData, cases: [...formData.cases, { input: "", expected: "" }] });
  const removeCase = (idx) => setFormData({ ...formData, cases: formData.cases.filter((_, i) => i !== idx) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: formData.title,
        difficulty: formData.difficulty,
        prompt: formData.prompt,
        tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean),
        starter_codes: {
           "python": formData.pythonStarter,
           "java": formData.javaStarter,
           "javascript": "// nodejs standard input logic here\n"
        },
        test_cases: formData.cases.map(c => ({
            input: c.input.replace(/\\n/g, "\n"), 
            expected: c.expected.trim()
        }))
      };

      await api.post("/coding/problems", payload);
      toast.success("Problem Deployed Successfully!");
      setModalOpen(false);
      setFormData({
        title: "", difficulty: "Easy", prompt: "", tags: "",
        pythonStarter: "def solution():\n    pass",
        javaStarter: "public class Solution { ... }",
        cases: [{ input: "", expected: "" }]
      });
      load();
    } catch (err) {
      toast.error("Failed to publish problem. Verify all inputs.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete permanently?")) return;
    try {
      await api.delete(`/coding/problems/${id}`);
      toast.success("Purged");
      load();
    } catch { toast.error("Failed delete"); }
  };

  const loadSubmissions = async (problem) => {
    setSubsLoading(true);
    setViewingSubmissions(true);
    setActiveProblemTitle(problem.title);
    setSubSearch("");
    try {
      const res = await api.get("/coding/submissions");
      setSubmissions(res.data.filter(s => s.problem_id === problem.id));
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setSubsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white dark:bg-slate-950">
      <Sidebar role="teacher" />
      <main className="flex-1 p-6 sm:p-8 lg:ml-64 lg:p-12 overflow-y-auto">
        <div className="space-y-8">
      <div className="flex justify-between items-center">
         <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white flex items-center gap-3">
               <TerminalSquare size={32} className="text-indigo-600" /> Curriculum Engineering
            </h1>
            <p className="text-slate-500 mt-1">Design and publish sophisticated logic puzzles to the Student Arena.</p>
         </div>
         <button 
           onClick={() => setModalOpen(true)}
           className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold flex items-center gap-2 shadow-xl shadow-indigo-600/20 transition-all"
         >
            <Plus size={18} /> Forge New Task
         </button>
      </div>

      {loading ? (
         <div className="h-64 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-500/20 border-t-indigo-600 rounded-full animate-spin" /></div>
      ) : problems.length === 0 ? (
         <div className="bg-white dark:bg-slate-900 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-[2.5rem] p-24 text-center flex flex-col items-center">
            <BrainCircuit size={48} className="text-indigo-300 dark:text-indigo-900 mb-4" />
            <h3 className="text-xl font-black text-slate-900 dark:text-white">Problem Vault Empty</h3>
            <button onClick={() => setModalOpen(true)} className="mt-4 text-indigo-600 font-bold hover:underline">Deploy first puzzle</button>
         </div>
      ) : (
         <div className="grid gap-4">
            {problems.map(p => (
               <div key={p.id} className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-5 flex items-center justify-between shadow-sm">
                  <div>
                     <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-black uppercase px-2 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded">{p.difficulty}</span>
                        <span className="text-xs font-mono text-slate-400">#ID-{p.id}</span>
                     </div>
                     <h4 className="font-black text-slate-900 dark:text-white">{p.title}</h4>
                  </div>
                  <div className="flex gap-2">
                     <button onClick={() => loadSubmissions(p)} className="p-2 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-colors" title="View Submissions">
                        <Users size={18} />
                     </button>
                     <button onClick={() => handleDelete(p.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors" title="Delete Problem">
                        <Trash2 size={18} />
                     </button>
                  </div>
               </div>
            ))}
         </div>
      )}

      {/* Full Overlay Modal for creation */}
      {modalOpen && (
         <div className="fixed inset-0 bg-slate-900/90 z-50 overflow-y-auto p-4 md:p-12 backdrop-blur-sm flex justify-center">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] w-full max-w-4xl shadow-2xl flex flex-col overflow-hidden border border-white/10">
               <div className="p-6 border-b dark:border-slate-800 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                     <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-xl"><Save size={20}/></div>
                     <h2 className="text-xl font-black dark:text-white">Configure Logic Objective</h2>
                  </div>
                  <button onClick={() => setModalOpen(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors dark:text-white"><X size={20}/></button>
               </div>
               
               <form onSubmit={handleSubmit} className="p-8 overflow-y-auto flex-1 grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                     <div>
                        <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Title</label>
                        <input 
                          required 
                          className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-0 rounded-xl text-slate-900 dark:text-white focus:ring-2 ring-indigo-500 font-bold"
                          placeholder="E.g., Fibonacci Sequence"
                          value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})}
                        />
                     </div>
                     
                     <div className="grid grid-cols-2 gap-4">
                        <div>
                           <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Intensity</label>
                           <select 
                              className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-0 rounded-xl font-bold dark:text-white"
                              value={formData.difficulty} onChange={e => setFormData({...formData, difficulty: e.target.value})}
                           >
                              <option>Easy</option><option>Medium</option><option>Hard</option>
                           </select>
                        </div>
                        <div>
                           <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Labels (CSV)</label>
                           <input 
                             className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-0 rounded-xl font-bold dark:text-white"
                             placeholder="Recursion, Math"
                             value={formData.tags} onChange={e => setFormData({...formData, tags: e.target.value})}
                           />
                        </div>
                     </div>

                     <div>
                        <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Prompt / Constraints</label>
                        <textarea 
                          required rows={6}
                          className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-0 rounded-xl font-medium text-sm dark:text-white"
                          placeholder="Define what the student needs to build..."
                          value={formData.prompt} onChange={e => setFormData({...formData, prompt: e.target.value})}
                        />
                     </div>

                     <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 p-4 rounded-xl flex gap-3 items-start">
                        <AlertCircle size={18} className="text-blue-600 shrink-0" />
                        <p className="text-xs text-blue-800 dark:text-blue-300 font-medium leading-relaxed">
                           Students receive code through standard streams. Use <code>\n</code> to separate lines in test inputs if the program expects multiple inputs.
                        </p>
                     </div>
                  </div>

                  <div className="space-y-6">
                     <div>
                        <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1"><Code size={14}/> Starter (Python)</label>
                        <textarea 
                          rows={4}
                          className="w-full px-4 py-3 bg-[#0d1117] border-0 rounded-xl text-green-400 font-mono text-xs"
                          value={formData.pythonStarter} onChange={e => setFormData({...formData, pythonStarter: e.target.value})}
                        />
                     </div>

                     <div className="space-y-4">
                        <div className="flex justify-between items-center">
                           <label className="block text-xs font-black text-slate-500 uppercase tracking-widest">Validation Rules (Test Cases)</label>
                           <button type="button" onClick={addCase} className="text-[10px] font-bold text-indigo-600 uppercase hover:underline flex items-center gap-1"><Plus size={12}/> Append Case</button>
                        </div>
                        
                        <div className="space-y-3 max-h-[280px] overflow-y-auto pr-2 custom-scrollbar">
                           {formData.cases.map((tc, i) => (
                              <div key={i} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl relative border dark:border-slate-700">
                                 <button type="button" onClick={() => removeCase(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500"><X size={14}/></button>
                                 <div className="grid grid-cols-2 gap-3">
                                    <div>
                                       <span className="text-[10px] font-bold text-slate-400 block mb-1">STDIN Input</span>
                                       <input 
                                         required className="w-full text-xs font-mono bg-white dark:bg-slate-900 border-0 px-2 py-1.5 rounded"
                                         value={tc.input} onChange={e => handleCaseChange(i, 'input', e.target.value)}
                                         placeholder="e.g. 5"
                                       />
                                    </div>
                                    <div>
                                       <span className="text-[10px] font-bold text-slate-400 block mb-1">Expected STDOUT</span>
                                       <input 
                                         required className="w-full text-xs font-mono bg-white dark:bg-slate-900 border-0 px-2 py-1.5 rounded"
                                         value={tc.expected} onChange={e => handleCaseChange(i, 'expected', e.target.value)}
                                         placeholder="e.g. 120"
                                       />
                                    </div>
                                 </div>
                              </div>
                           ))}
                        </div>
                     </div>
                     
                     <div className="pt-4 border-t dark:border-slate-800">
                        <button type="submit" className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-all shadow-xl shadow-indigo-600/30">
                           <TerminalSquare size={18}/> Deploy Problem Live
                        </button>
                     </div>
                  </div>
               </form>
            </div>
         </div>
      )}
      {/* Submissions Modal */}
      {viewingSubmissions && (
         <div className="fixed inset-0 bg-slate-900/90 z-50 overflow-y-auto p-4 md:p-12 backdrop-blur-sm flex justify-center items-start">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] w-full max-w-5xl shadow-2xl flex flex-col border border-white/10 min-h-[50vh]">
               <div className="p-6 border-b dark:border-slate-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="flex items-center gap-3">
                     <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-xl"><Users size={20}/></div>
                     <h2 className="text-xl font-black dark:text-white">Submissions: {activeProblemTitle}</h2>
                  </div>
                  <div className="flex items-center gap-3 w-full md:w-auto">
                     <input
                       type="text"
                       placeholder="Search name or email..."
                       value={subSearch}
                       onChange={e => setSubSearch(e.target.value)}
                       className="px-4 py-2 bg-slate-50 dark:bg-slate-800 border-0 rounded-xl text-sm w-full md:w-64 dark:text-white focus:ring-2 ring-emerald-500"
                     />
                     <button onClick={() => setViewingSubmissions(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors dark:text-white"><X size={20}/></button>
                  </div>
               </div>
               <div className="p-8 overflow-y-auto max-h-[70vh]">
                 {subsLoading ? (
                   <div className="flex justify-center p-10"><div className="w-8 h-8 border-4 border-emerald-500/20 border-t-emerald-600 rounded-full animate-spin" /></div>
                 ) : submissions.length === 0 ? (
                   <div className="text-center p-10 text-slate-500">No submissions yet for this problem.</div>
                 ) : (
                   <div className="space-y-6">
                     {submissions.filter(sub => {
                       if (!subSearch) return true;
                       const q = subSearch.toLowerCase();
                       return (sub.student_name && sub.student_name.toLowerCase().includes(q)) || 
                              (sub.student_email && sub.student_email.toLowerCase().includes(q));
                     }).map((sub) => (
                       <div key={sub.id} className="border dark:border-slate-800 rounded-2xl p-6 bg-slate-50 dark:bg-slate-800/50">
                         <div className="flex justify-between items-start mb-4">
                            <div>
                               <div className="text-sm font-bold dark:text-white">{sub.student_name || `Student ID: ${sub.user_id}`}</div>
                               {sub.student_email && <div className="text-xs text-slate-500 mt-0.5">{sub.student_email}</div>}
                               <div className="text-xs text-slate-500 mt-1">{new Date(sub.created_at).toLocaleString()}</div>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${sub.status === 'Passed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                               {sub.status === 'Passed' ? <CheckCircle size={14}/> : <XCircle size={14}/>}
                               {sub.status} ({sub.passed_count}/{sub.total_count})
                            </div>
                         </div>
                         <div className="bg-[#0d1117] rounded-xl p-4 overflow-x-auto">
                            <div className="text-[10px] uppercase font-bold text-slate-500 mb-2">{sub.language}</div>
                            <pre className="text-xs font-mono text-emerald-400">{sub.source_code}</pre>
                         </div>
                       </div>
                     ))}
                   </div>
                 )}
               </div>
            </div>
         </div>
      )}
        </div>
      </main>
    </div>
  );
}
